/* eslint-disable @typescript-eslint/indent */
import React, { FC, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, useLocation, useParams } from 'react-router-dom';
import { Loader } from '@sdir/sds';
import * as Yup from 'yup';
import styled from 'styled-components';
import { Field, Form, Formik } from 'formik';
import { AttachmentType } from '@sdir/httpclient/lib/clients/aps/application';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import NewApplicationLayout from '../../NewApplicationLayout';
import { routes } from '../../../../constants/routes';
import { Card, InputError } from '../../../Atoms';
import { FormAttachmentGroup, SubmitSequenceSummary } from '../../../Molecules';
import {
  useCurrentApplication,
  useSubmitSequence,
  AttachmentGroupChanges,
} from '../../../../services/hooks';
import {
  QUALIFICATION_URI,
  findQualification,
  getFromStorage,
} from '../../../../helpers/Qualifications';
import {
  buildCreateAttachmentGroupSequence,
  buildSubmitAttachmentUpdatesSequence,
  CreateAttachmentGroupTracker,
  SubmitAttachmentUpdatesParams,
  SubmitAttachmentUpdatesTracker,
} from '../../../../helpers/attachmentApiHelpers';
import EditApplicationGuard from '../../../Molecules/EditApplicationGuard/EditApplicationGuard';
import { FileViewer } from '../../../Molecules/FileDrop/FileViewer';
import { Loadable } from '../../../../types';
import { useNavigateWithLang } from '../../../../services/hooks/useNavigateWithLang';

export const AddCertificate: FC = () => {
  const intl = useIntl();
  const navigate = useNavigateWithLang();
  const { attachmentGroup, applicationUid } = useParams<{
    attachmentGroup: string;
    applicationUid: string;
  }>();
  const [activeFile, setActiveFile] = useState<Loadable<File> | null>();
  const attachmentGroupFormatted = attachmentGroup === 'new' ? null : attachmentGroup;
  const uriFromStorage = getFromStorage(QUALIFICATION_URI);

  const { application, refresh, qualifications, loadQualifications } = useCurrentApplication({
    includeQualifications: true,
  });
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const fromMissingDocumentation = searchParams.get('from') === 'missingdocumentation';

  const attachmentsForGroup = useMemo(
    () =>
      application.result?.attachments?.filter((a) => a.group === attachmentGroupFormatted) || [],
    [application.result?.attachments, attachmentGroupFormatted]
  );

  const groupQualificationUri =
    attachmentsForGroup?.[0]?.simulatorResultItemUri || uriFromStorage || null;

  const { submit: submitAttachmentGroup } = useSubmitSequence(buildCreateAttachmentGroupSequence);
  const {
    isSubmitting,
    results,
    submit: submitAttachments,
  } = useSubmitSequence<SubmitAttachmentUpdatesParams, SubmitAttachmentUpdatesTracker>(
    buildSubmitAttachmentUpdatesSequence
  );

  const [showValidation, setShowValidation] = useState(false);

  const [submitAttachmentsTracker, setSubmitAttachmentsTracker] =
    useState<SubmitAttachmentUpdatesTracker>({});
  const [submitAttachmentGroupTracker, setSubmitAttachmentGroupTracker] =
    useState<CreateAttachmentGroupTracker>({});

  const initialValues = useMemo((): any => {
    return {
      attachments: { newFiles: [], removedAttachments: [], updatedAttachments: [] },
    };
  }, []);

  const qualification = useMemo(() => {
    if (!qualifications.result || !groupQualificationUri) return null;
    return findQualification(qualifications.result, groupQualificationUri);
  }, [qualifications.result, groupQualificationUri]);

  const handleExit = useCallback(() => {
    const route = fromMissingDocumentation
      ? routes.application.uploadMissingDocumentation
      : routes.application.qualification;
    navigate(generatePath(route, { applicationUid }));
  }, [navigate, applicationUid, fromMissingDocumentation]);

  const handleActiveFileChange = useCallback(
    (file: Loadable<File> | null) => {
      if (file !== activeFile) {
        setActiveFile(file);
      }
    },
    [activeFile]
  );

  const hasGroupFiles = !!attachmentsForGroup.length;

  const handleSubmit = useCallback(
    async (values) => {
      const { updatedAttachments, removedAttachments, newFiles } = values.attachments;
      if (!application.result) {
        setShowValidation(true);
        return;
      }

      let newAttachmentTracker: SubmitAttachmentUpdatesTracker | null = null;
      let newAttachmentGroupTracker: CreateAttachmentGroupTracker | null = null;
      let group = attachmentGroupFormatted;
      if (!group) {
        // Create new attachment group
        const { tracker: attachmentGroupTracker } = await submitAttachmentGroup(
          {
            application: application.result,
            attachmentType: AttachmentType.Certificate,
            files: newFiles,
            simulatorResultItemUri: groupQualificationUri,
          },
          submitAttachmentGroupTracker
        );
        group =
          Object.values(attachmentGroupTracker?.addedAttachments || {})[0]?.result?.group || null;
        newAttachmentGroupTracker = attachmentGroupTracker;
      } else {
        // Submit attachments, when we already have an attachment group
        const { tracker: attachmentTracker } = await submitAttachments(
          {
            application: application.result,
            attachmentGroup: group,
            qualificationUri: groupQualificationUri,
            updatedAttachments,
            removedAttachments,
            newAttachments: newFiles.map((f) => ({ file: f, type: AttachmentType.Certificate })),
          },
          submitAttachmentsTracker
        );
        group = attachmentTracker?.newAttachments?.[0]?.result?.group || null;
        newAttachmentTracker = attachmentTracker;
      }

      if (newAttachmentTracker?.errors || newAttachmentGroupTracker?.errors) {
        if (newAttachmentTracker) setSubmitAttachmentsTracker(newAttachmentTracker);
        if (newAttachmentGroupTracker) setSubmitAttachmentGroupTracker(newAttachmentGroupTracker);
      } else {
        refresh?.();
        loadQualifications?.();
        handleExit();
      }
    },
    [
      application.result,
      attachmentGroupFormatted,
      groupQualificationUri,
      handleExit,
      refresh,
      loadQualifications,
      submitAttachments,
      submitAttachmentGroup,
      submitAttachmentsTracker,
      submitAttachmentGroupTracker,
    ]
  );

  const validation = useMemo(
    () =>
      Yup.object().shape({
        attachments: Yup.object().test(
          'has-files',
          intl.formatMessage({ id: 'general.form.error' }),
          (v: any) => {
            if (!v) return attachmentsForGroup.length > 0;
            const changes = v as AttachmentGroupChanges;
            const resultingAttachmentCount =
              attachmentsForGroup.length +
              changes.newFiles.length -
              changes.removedAttachments.length;
            return resultingAttachmentCount > 0;
          }
        ),
      }),
    [attachmentsForGroup.length, intl]
  );

  const heading = qualification
    ? `${qualification.title}: ${qualification.description}`
    : intl.formatMessage({ id: 'addcertificate.heading' });

  return (
    <EditApplicationGuard>
      {application.loading ? (
        <Loader />
      ) : (
        <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validation}>
          {({ submitForm }) => (
            <Form noValidate>
              <NewApplicationLayout
                exitLabel={intl.formatMessage({ id: 'general.form.back' })}
                mainContentMaxWidth="100%"
                canNavBack
                canNavNext={!isSubmitting}
                navBackLabel={intl.formatMessage({ id: 'general.form.goback' })}
                navNextLabel={intl.formatMessage({ id: 'general.form.next' })}
                onNavNextClick={submitForm}
                onNavBackClick={handleExit}
              >
                <CertificateFormContent>
                  <div>
                    <Card heading={heading}>
                      <p>{intl.formatMessage({ id: 'addcertificate.description' })}</p>
                      <Field
                        className="file-input"
                        name="attachments"
                        group={attachmentGroupFormatted}
                        component={FormAttachmentGroup}
                        onlyFileList
                        onChangeActiveFile={handleActiveFileChange}
                      />
                    </Card>
                  </div>
                  <RightSection>
                    {activeFile?.loading && <Loader />}
                    {activeFile?.error && (
                      <ErrorMessage
                        title={intl.formatMessage({ id: 'general.error.failedtoloadattachment' })}
                        error={activeFile.error}
                      />
                    )}
                    {activeFile?.result && (
                      <FileViewer
                        file={activeFile.result}
                        handleDocumentError={(e) => setActiveFile({ error: e })}
                      />
                    )}
                    {!hasGroupFiles && showValidation && (
                      <InputError>{intl.formatMessage({ id: 'general.form.error' })}</InputError>
                    )}
                  </RightSection>
                </CertificateFormContent>
                {isSubmitting && <Loader />}
                <SubmitSequenceSummary results={results} />
              </NewApplicationLayout>
            </Form>
          )}
        </Formik>
      )}
    </EditApplicationGuard>
  );
};

const CertificateFormContent = styled.div`
  display: grid;
  grid-template-rows: auto auto;
  grid-row-gap: 3rem;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 32px;
  margin-top: 3rem;
  justify-content: stretch;

  > * {
    position: relative;
    min-height: calc(100vh - 400px);
  }

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    grid-template-columns: 1fr;

    > div:first-child {
      min-height: 0;
    }

    > div:nth-child(2) {
      min-height: 500px;
    }
  }
`;

const RightSection = styled.div`
  max-height: 1000px;
  overflow: auto;
`;
